(() => {
  const key = 'lonvory:rubik:expanded-navigation';
  const mobile = matchMedia('(max-width: 767px)');
  const expanded = new Set();
  const hosts = new Set();
  const busy = new WeakSet();
  const observed = new WeakSet();
  const isProduct = path => /\/products\/[^/]+\/?$/.test(path);
  if (!isProduct(location.pathname)) return;
  try {
    const pending = JSON.parse(sessionStorage.getItem(key) || 'null');
    sessionStorage.removeItem(key);
    if (performance.getEntriesByType('navigation')[0]?.type === 'navigate'
      && pending?.path === location.pathname && Date.now() - pending.time < 60000
      && Array.isArray(pending.groups)) pending.groups.forEach(name => expanded.add(name));
  } catch { /* Storage is optional; the selection remains usable. */ }
  const css = `
    [data-lonvory-rubik-hidden] { display: none !important; }
    .lonvory-rubik-more {
      display: flex; align-items: center; justify-content: center; gap: 10px;
      width: 100%; min-height: 44px; box-sizing: border-box;
      margin: 10px 0 0; padding: 12px 16px; border: 1px solid #222;
      border-radius: 9px; background: #f6f6f6; color: #111;
      font: 600 14px/1.4 var(--rcl-font-family, sans-serif); cursor: pointer;
    }
    .lonvory-rubik-more:hover { background: #ececec; }
    .lonvory-rubik-more:focus-visible { outline: 2px solid #111; outline-offset: 3px; }
    .lonvory-rubik-more svg { display: block; width: 16px; height: 16px; flex: 0 0 16px; align-self: center; }
  `;
  const productHost = host => host instanceof HTMLElement
    && host.localName === 'rubik-combined-listings' && !!host.closest('main')
    && !host.closest('product-card, product-recommendations, quick-add-dialog, dialog, .rcl-product-card-auto')
    && !!host.closest('.rcl-product-page-auto, [id^="ProductInformation-"], .product-information, .product__info-container');
  const groupName = group => group.querySelector('.rcl__group-label-option-name')?.textContent.trim() || 'Optionen';
  function render(host) {
    if (!productHost(host) || !host.shadowRoot || busy.has(host)) return;
    const root = host.shadowRoot;
    hosts.add(host);
    busy.add(host);
    try {
      root.querySelectorAll('.lonvory-rubik-more').forEach(button => button.remove());
      root.querySelectorAll('[data-lonvory-rubik-hidden]').forEach(item => item.removeAttribute('data-lonvory-rubik-hidden'));
      if (!root.querySelector('style[data-lonvory-rubik]')) {
        const style = document.createElement('style');
        style.dataset.lonvoryRubik = '';
        style.textContent = css;
        root.append(style);
      }
      // Consume native limits; keep Rubik's original links and availability rules.
      for (const grid of root.querySelectorAll('.rcl__grid')) {
        if (grid.hasAttribute('data-limit-to-one-row')) {
          grid.removeAttribute('data-limit-to-one-row');
          grid.querySelectorAll('.rcl__swatch').forEach(item => { item.style.display = ''; });
        }
        const overflow = grid.querySelector('.rcl__swatch--overflow');
        if (overflow) {
          const focus = root.activeElement || document.activeElement;
          const x = scrollX, y = scrollY;
          overflow.click();
          if (focus instanceof HTMLElement) focus.focus({ preventScroll: true });
          scrollTo(x, y);
        }
      }
      if (!mobile.matches) return;
      for (const group of root.querySelectorAll('.rcl[data-rcl-group]')) {
        const name = groupName(group);
        const items = Array.from(group.querySelectorAll('.rcl__grid .rcl__swatch:not(.rcl__swatch--overflow)'));
        if (items.length <= 8 || expanded.has(name)) continue;
        const visible = new Set(items.slice(0, 8));
        const selected = items.find(item => item.classList.contains('rcl__swatch--active'));
        if (selected && !visible.has(selected)) { visible.delete(items[7]); visible.add(selected); }
        const hidden = items.filter(item => !visible.has(item));
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lonvory-rubik-more';
        button.setAttribute('aria-expanded', 'false');
        const label = document.createElement('span');
        const de = document.documentElement.lang.toLowerCase().startsWith('de');
        label.textContent = de ? `${hidden.length} weitere ${hidden.length === 1 ? 'Option' : 'Optionen'} anzeigen`
          : `Show ${hidden.length} more ${hidden.length === 1 ? 'option' : 'options'}`;
        const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.setAttribute('focusable', 'false');
        arrow.setAttribute('viewBox', '0 0 16 16');
        const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        chevron.setAttribute('d', 'M4 6 L8 10 L12 6');
        chevron.setAttribute('fill', 'none');
        chevron.setAttribute('stroke', 'currentColor');
        chevron.setAttribute('stroke-width', '1.75');
        chevron.setAttribute('stroke-linecap', 'round');
        chevron.setAttribute('stroke-linejoin', 'round');
        arrow.append(chevron);
        button.append(label, arrow);
        button.addEventListener('click', () => {
          expanded.add(name);
          hidden.forEach(item => item.removeAttribute('data-lonvory-rubik-hidden'));
          button.setAttribute('aria-expanded', 'true');
          button.remove();
          hidden[0]?.focus({ preventScroll: true });
        });
        group.append(button);
        hidden.forEach(item => item.setAttribute('data-lonvory-rubik-hidden', ''));
      }
    } finally { busy.delete(host); }
  }
  function connect(host) {
    if (!productHost(host)) return;
    if (!observed.has(host)) {
      observed.add(host);
      host.addEventListener('rcl:ready', () => render(host));
    }
    render(host);
  }
  document.addEventListener('rcl:ready', event => connect(event.target), true);
  document.querySelectorAll('rubik-combined-listings').forEach(connect);
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches('rubik-combined-listings')) connect(node);
      node.querySelectorAll('rubik-combined-listings').forEach(connect);
    }
    hosts.forEach(host => { if (!host.isConnected) hosts.delete(host); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  mobile.addEventListener('change', () => hosts.forEach(render));
  document.addEventListener('click', event => {
    if (!mobile.matches || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const path = event.composedPath();
    const host = path.find(productHost);
    const link = path.find(node => node instanceof HTMLAnchorElement);
    if (!host || !link?.classList.contains('rcl__swatch')) return;
    const target = new URL(link.href, location.href);
    if (target.origin !== location.origin || !isProduct(target.pathname)) return;
    try {
      sessionStorage.removeItem(key);
      if (expanded.size) sessionStorage.setItem(key, JSON.stringify({
        path: target.pathname, groups: Array.from(expanded), time: Date.now(),
      }));
    } catch { /* Never block a variant switch when storage is unavailable. */ }
  }, true);
})();
